import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import useInterval from 'hooks/useInterval'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import useHasMounted from 'hooks/useHasMounted'

const FixedWidthCountdown = styled.div`
  display: inline-block;

  &.fixed-width {
    width: 13ch;
  }
`

const DEFAULT_HOURS_TO_BOOK = 100

interface Props {
  className?: string;
  hoursToBook?: number
  endDate: string;
  showHourOnly?: boolean;
  isLuxPlusEarlyAccess?: boolean;
}

function OfferUrgencyCountdownLabel(props: Props) {
  const {
    endDate,
    hoursToBook = DEFAULT_HOURS_TO_BOOK,
    showHourOnly = false,
    isLuxPlusEarlyAccess = false,
    ...rest
  } = props
  const [now, setNow] = useState<moment.Moment>(moment())
  const luxPlusEnabled = useAppSelector(state => isLuxPlusEnabled(state))

  const hasMounted = useHasMounted()

  const {
    message,
    timeLeft,
  } = useMemo(() => {
    const end = moment(endDate)
    const leftToTheEnd = moment.duration(end.diff(now))
    const hoursLeft = leftToTheEnd.asHours()

    if (hoursLeft <= 0 || hoursLeft >= hoursToBook) {
      return {
        message: undefined,
        timeLeft: undefined,
      }
    }

    const message = isLuxPlusEarlyAccess && luxPlusEnabled ? 'Priority access ends in' : 'Offer ends in'

    const totalHours = Math.floor(hoursLeft)
    const minutes = leftToTheEnd.minutes()
    const seconds = leftToTheEnd.seconds()

    let timeLeftString
    if (showHourOnly) {
      // Round up hours if there are any minutes or seconds
      const roundedHours = minutes > 0 || seconds > 0 ? totalHours + 1 : totalHours
      timeLeftString = `${String(roundedHours).padStart(2, '0')}h`
    } else {
      timeLeftString = `${String(totalHours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`
    }

    return {
      message,
      timeLeft: <FixedWidthCountdown className={cn({ 'fixed-width': !showHourOnly })}>
        {timeLeftString}
      </FixedWidthCountdown>,
    }
  }, [endDate, now, isLuxPlusEarlyAccess, luxPlusEnabled, hoursToBook, showHourOnly])

  // only update per minute if we only show hr
  const updateInterval = showHourOnly ? 60000 : 1000

  useInterval(() => setNow(moment()), updateInterval)

  if (!message) {
    return null
  }

  return (
    <UrgencyLabel
      {...rest}
      className={cn(rest.className, { 'not-mounted': !hasMounted })}
      variant="critical2"
    >
      {message}{' '}{hasMounted && timeLeft}
    </UrgencyLabel>
  )
}

export default OfferUrgencyCountdownLabel
